import { IClinicHolidayRow } from '@dataTypes/clinic-holidays.type'
import { IClinicOverrideRow } from '@dataTypes/clinic-overrides.type'
import { amPmLabelToHms, formatHmsAsAmPm } from '@utils/phoneManagement.utils'

export type OpeningHoursDayKey =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'

export type DaySchedule = {
  enabled: boolean
  startTime: string
  endTime: string
}

export type DateOverrideRow = {
  id: string
  description: string
  date: string
  overrideHours: string
  overrideType: 'adjusted' | 'closed'
}

export type HolidayRow = {
  id: string
  description: string
  date: string
  duration: string
  holidayType: 'public' | 'private'
}

export type AddOverrideFormData = {
  description: string
  date: string
  overrideType: 'adjusted' | 'closed'
  startTime: string
  endTime: string
}

export type AddHolidayFormData = {
  description: string
  date: string
  holidayType: 'public' | 'private'
  startTime: string
  endTime: string
}

export const OPENING_HOURS_DAYS: OpeningHoursDayKey[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export const DEFAULT_DAY_SCHEDULE: DaySchedule = {
  enabled: true,
  startTime: '8:00 AM',
  endTime: '6:00 PM',
}

export const createDefaultWeekSchedule = (): Record<
  OpeningHoursDayKey,
  DaySchedule
> => ({
  Sunday: { enabled: false, startTime: '8:00 AM', endTime: '6:00 PM' },
  Monday: { ...DEFAULT_DAY_SCHEDULE },
  Tuesday: { ...DEFAULT_DAY_SCHEDULE },
  Wednesday: { ...DEFAULT_DAY_SCHEDULE },
  Thursday: { ...DEFAULT_DAY_SCHEDULE },
  Friday: { ...DEFAULT_DAY_SCHEDULE },
  Saturday: { ...DEFAULT_DAY_SCHEDULE },
})

export const formatOpeningHoursDateUs = (dateStr: string): string => {
  const part = dateStr.split('T')[0]
  const [y, m, d] = part.split('-')
  if (!y || !m || !d) return dateStr
  return `${m}/${d}/${y}`
}

export const formatTimeRangeLabel = (
  start: string | null,
  end: string | null,
): string => {
  if (!start || !end) return '—'
  return `${formatHmsAsAmPm(start)} → ${formatHmsAsAmPm(end)}`
}

export const overrideApiToGridRow = (
  row: IClinicOverrideRow,
): DateOverrideRow => ({
  id: row.clinic_override_id ?? '',
  description: row.description,
  date: formatOpeningHoursDateUs(row.override_date),
  overrideHours:
    row.override_type === 'closed'
      ? 'Closed'
      : formatTimeRangeLabel(row.start_time, row.end_time),
  overrideType: row.override_type,
})

export const holidayApiToGridRow = (row: IClinicHolidayRow): HolidayRow => ({
  id: row.clinic_holiday_id ?? '',
  description: row.description,
  date: formatOpeningHoursDateUs(row.holiday_date),
  duration: formatTimeRangeLabel(row.start_time, row.end_time),
  holidayType: row.holiday_type,
})

const toDateInputValue = (dateStr: string): string => dateStr.split('T')[0]

export const overrideApiToForm = (
  row: IClinicOverrideRow,
): AddOverrideFormData => ({
  description: row.description,
  date: toDateInputValue(row.override_date),
  overrideType: row.override_type,
  startTime: row.start_time ? formatHmsAsAmPm(row.start_time) : '8:00 AM',
  endTime: row.end_time ? formatHmsAsAmPm(row.end_time) : '6:00 PM',
})

export const holidayApiToForm = (
  row: IClinicHolidayRow,
): AddHolidayFormData => ({
  description: row.description,
  date: toDateInputValue(row.holiday_date),
  holidayType: row.holiday_type,
  startTime: row.start_time ? formatHmsAsAmPm(row.start_time) : '8:00 AM',
  endTime: row.end_time ? formatHmsAsAmPm(row.end_time) : '6:00 PM',
})

export const overrideFormToApiRow = (
  form: AddOverrideFormData,
  id?: string,
): IClinicOverrideRow => ({
  ...(id ? { clinic_override_id: id } : {}),
  description: form.description,
  override_date: form.date,
  override_type: form.overrideType,
  start_time:
    form.overrideType === 'adjusted' ? amPmLabelToHms(form.startTime) : null,
  end_time:
    form.overrideType === 'adjusted' ? amPmLabelToHms(form.endTime) : null,
})

export const holidayFormToApiRow = (
  form: AddHolidayFormData,
  id?: string,
): IClinicHolidayRow => ({
  ...(id ? { clinic_holiday_id: id } : {}),
  description: form.description,
  holiday_date: form.date,
  holiday_type: form.holidayType,
  start_time: amPmLabelToHms(form.startTime),
  end_time: amPmLabelToHms(form.endTime),
})
